/**
 * Which flows the pointer is currently over IN THE SOURCE DOCUMENT.
 *
 * `FlowRow` is memo'd and deliberately subscribes to hover ACTIONS only, never
 * to hover state — a context here would re-render every row (and every cell)
 * on every band the pointer crosses. This store's per-row snapshot is a
 * BOOLEAN, so React bails out on every row whose value did not flip: exactly
 * one row re-renders when the hover lands. Mirrors `aiInFlightStore`'s shape.
 */

import { useSyncExternalStore } from "react";

type Listener = () => void;

const listeners = new Set<Listener>();
const EMPTY_IDS: readonly string[] = Object.freeze([]);
let hoveredIds: readonly string[] = EMPTY_IDS;

const sameIds = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

export const sourceHover = {
  setHoveredFlowIds(ids: string[]): void {
    if (sameIds(hoveredIds, ids)) return;
    hoveredIds = ids.length === 0 ? EMPTY_IDS : ids;
    for (const listener of listeners) listener();
  },
  clear(): void {
    sourceHover.setHoveredFlowIds([]);
  },
  getIds(): readonly string[] {
    return hoveredIds;
  },
};

export function subscribeSourceHover(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isSourceHovered(flowId: string): boolean {
  return hoveredIds.includes(flowId);
}

/** Re-renders ONLY when this flow's hovered-ness flips. */
export function useIsSourceHovered(flowId: string): boolean {
  return useSyncExternalStore(subscribeSourceHover, () =>
    isSourceHovered(flowId),
  );
}

/** The full id list — the document's own strong-band layer (PreviewContent,
 *  STK-2338) needs the SET, not one flow's membership, so every box under the
 *  pointer strengthens, not just one. `getIds` returns a stable reference
 *  between writes (see the store test), so this cannot loop. */
export function useSourceHoverIds(): readonly string[] {
  return useSyncExternalStore(subscribeSourceHover, sourceHover.getIds);
}

/** Resets DATA only — leaves subscriptions intact, mirroring the sibling stores. */
export function resetSourceHoverStore(): void {
  hoveredIds = EMPTY_IDS;
}

/** The `SourceHoverHandler` shape every viewer emits, bound to this store. */
export const writeSourceHover = (ids: string[] | null): void => {
  if (ids?.length) sourceHover.setHoveredFlowIds(ids);
  else sourceHover.clear();
};
